/** @jsx jsx */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { jsx } from '@emotion/core';
import {
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DoneIcon from '@material-ui/icons/Done';
import CrossIcon from '@material-ui/icons/Clear';

import {
  ICustomTheme,
  IChallengeDeclaration,
  DeclarationStatusType,
} from 'interfaces';
import useQuery from 'core/utils/use-query.hook';
import { StyledBubble } from 'components/pages/History/styles';
import moment from 'moment';
import { RESULT_STATUS } from 'constants/index';
import ProductsDetail from './ProductsDetail';
import QuestionsDetail from './QuestionsDetail';
import DeclarationCommentSection from './DeclarationCommentSection';
import {
  StyledAdditionalEndDate,
  StyledAdditionalQuestion,
  StyledAdditionalQuestionsContainer,
  StyledAdditionalQuestionsDatesContainer,
  StyledAdditionalQuestionsDistributorContainer,
  StyledAdditionalResponse,
  StyledBannerChip,
} from './styles';

function DeclarationsStatus({
  status,
}: {
  status: DeclarationStatusType;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();

  const Icon = status === 'refused' ? CrossIcon : DoneIcon;

  return (
    <StyledBubble
      css={{
        backgroundColor: (() => {
          switch (status) {
            case 'waiting':
              return theme.colors.grey.light;
            case 'refused':
              return theme.colors.error.main;
            default:
              return theme.colors.success.main;
          }
        })(),
      }}
    >
      <Icon />
    </StyledBubble>
  );
}

function DeclarationSummary({
  id,
  createdAt,
  declarationRewardValue,
  declarationResultValue,
  status,
  unitToDisplay,
  isGoalMecanicByProduct,
}: IChallengeDeclaration & {
  unitToDisplay: string;
  isGoalMecanicByProduct: boolean;
}): JSX.Element {
  const theme: ICustomTheme = useTheme();
  const { t } = useTranslation();
  const isValidatedDeclaration = status === RESULT_STATUS.VALIDATED;

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      css={{ gap: theme.spacing(2), marginRight: theme.spacing(1.5) }}
    >
      <Grid item>
        <Grid container alignItems="center" css={{ gap: theme.spacing(2) }}>
          <Grid item>
            <DeclarationsStatus status={status} />
          </Grid>
          <Grid item>
            <Typography>
              {t('challenges.declaration.history.declaration_date', {
                id,
                date: new Date(createdAt).toLocaleDateString(),
              })}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        {isValidatedDeclaration && (
          <Grid container justifyContent="center" alignItems="center">
            <Grid item>
              <StyledBannerChip
                label={(
                  <NumberFormat
                    value={
                      isGoalMecanicByProduct
                        ? declarationRewardValue || 0
                        : declarationResultValue || 0
                    }
                    displayType="text"
                    thousandSeparator=" "
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Typography
                variant="caption"
                style={{ color: theme.colors.paletteFromBO.main }}
              >
                {unitToDisplay} 
                {' '}
                {isGoalMecanicByProduct && '*'}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default function Declarations({
  isGoalMecanicByProduct,
  declarations,
  unitToDisplay,
}: {
  isGoalMecanicByProduct: boolean;
  declarations: IChallengeDeclaration[];
  unitToDisplay: any;
}): JSX.Element {
  const { t } = useTranslation();
  const isDetailsMinifiedMode = useMediaQuery('(max-width:1100px)');
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { type } = useQuery();
  const filteredDeclarations = useMemo(() => {
    const reversedDeclarations = [...declarations].reverse();

    return !type || type === 'all'
      ? reversedDeclarations
      : reversedDeclarations.filter(
        ({ status }: IChallengeDeclaration) => status === type,
      );
  }, [type, declarations]);

  return (
    <React.Fragment>
      {filteredDeclarations.map(
        (declaration: IChallengeDeclaration, index: number) => {
          const invoiceStartDate = declaration?.invoiceDateStart;
          const invoiceEndDate = declaration?.invoiceDateEnd;
          const validInvoiceStartDate = moment(invoiceStartDate).isValid()
            ? moment(invoiceStartDate).format('DD/MM/YYYY')
            : null;

          const validInvoiceEndDate = moment(invoiceEndDate).isValid()
            ? moment(invoiceEndDate).format('DD/MM/YYYY')
            : null;
          return (
            <Accordion
              defaultExpanded={!index && !type}
              key={declaration.id}
              square
              css={{
                boxShadow: 'none',
                borderBottom: `1px solid ${theme.colors.grey.light}`,
                '&:first-of-type': {
                  borderTop: `1px solid ${theme.colors.grey.light}`,
                },
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon style={{ fontSize: 30 }} />}
                css={{
                  marginRight: theme.spacing(1),
                  padding: theme.spacing(1, 0),
                }}
              >
                <DeclarationSummary
                  {...declaration}
                  unitToDisplay={unitToDisplay}
                  isGoalMecanicByProduct={isGoalMecanicByProduct}
                />
              </AccordionSummary>
              <StyledAdditionalQuestionsContainer>
                {(validInvoiceStartDate || validInvoiceEndDate) && (
                  <StyledAdditionalQuestionsDatesContainer>
                    <StyledAdditionalQuestion isMobile={isMobile}>
                      {t('challenges.declaration.history.proof_date')}
                    </StyledAdditionalQuestion>
                    {' '}
                    <StyledAdditionalResponse isMobile={isMobile}>
                      {validInvoiceStartDate}
                    </StyledAdditionalResponse>
                    {validInvoiceEndDate && (
                      <span>
                        {' '}
                        -
                        {' '}
                        <StyledAdditionalEndDate isMobile={isMobile}>
                          {validInvoiceEndDate}
                        </StyledAdditionalEndDate>
                      </span>
                    )}
                  </StyledAdditionalQuestionsDatesContainer>
                )}
                {declaration?.distributor && (
                  <StyledAdditionalQuestionsDistributorContainer>
                    <StyledAdditionalQuestion isMobile={isMobile}>
                      {t('challenges.declaration.history.distributor')}
                    </StyledAdditionalQuestion>
                    {' '}
                    <StyledAdditionalResponse isMobile={isMobile}>
                      {declaration?.distributor}
                    </StyledAdditionalResponse>
                  </StyledAdditionalQuestionsDistributorContainer>
                )}
              </StyledAdditionalQuestionsContainer>
              {declaration.comment && (
                <DeclarationCommentSection
                  comment={declaration.comment}
                  title={
                    declaration.status === RESULT_STATUS.REFUSED
                      ? t('challenges.declaration.history.refused_declaration')
                      : t(
                          'challenges.declaration.history.validated_declaration',
                        )
                  }
                  infoText={
                    declaration.status === RESULT_STATUS.REFUSED
                      ? t('challenges.declaration.history.refused_precision')
                      : t('challenges.declaration.history.validated_precision')
                  }
                  sectionColor={
                    declaration.status === RESULT_STATUS.REFUSED
                      ? theme.colors.error.main
                      : theme.colors.success.main
                  }
                />
              )}
              <ProductsDetail
                {...declaration}
                isMobile={isDetailsMinifiedMode}
                unitToDisplay={unitToDisplay}
                isGoalMecanicByProduct={isGoalMecanicByProduct}
              />
              {declaration.formEntry?.formValues?.length && (
                <QuestionsDetail
                  questions={declaration.formEntry?.formValues}
                  isMobile={isDetailsMinifiedMode}
                />
              )}
              {isGoalMecanicByProduct && (
                <div
                  style={{
                    margin: theme.spacing(2, 0),
                    fontSize: isMobile ? 14 : 16,
                  }}
                >
                  {t('challenges.declaration.history.thresholdInfo', {
                    rewardUnit: unitToDisplay,
                  })}
                </div>
              )}
            </Accordion>
          );
        },
      )}
    </React.Fragment>
  );
}
